// Recharts components
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsRTL } from '../../redux/selectors/displaySettingsSelectors';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Custom hooks
import { useChartConfig } from './hooks/useChartConfig';

// Shared components
import { ChartContainer } from './shared/ChartContainer';
import { ChartContent } from './shared/ChartContent';
import { ChartFooter } from './shared/ChartFooter';

interface QuestionsResponseData {
  AnswerTitle: string;
  AnswerCount: number;
}

interface QuestionsBarChartProps {
  data: QuestionsResponseData[];
}

export const QuestionsBarChart = ({ data }: QuestionsBarChartProps) => {
  const { t } = useTranslation();
  const isRTL = useAppSelector(selectIsRTL);

  // Chart configuration
  const config = useChartConfig();

  return (
    <ChartContainer>
      <style>
        {`
            .recharts-legend-item .recharts-surface {
              margin-${isRTL ? 'left' : 'right'}: 8px !important;
            }
          `}
      </style>
      <ChartContent>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart
            width={700}
            height={360}
            data={data}
            margin={{
              top: 25,
              right: isRTL ? 20 : 20,
              left: isRTL ? -20 : 0,
            }}
            barSize={20}
            onClick={undefined} // Disable click behavior
            style={config.styles.noOutline} // Remove focus outline
          >
            <XAxis
              dataKey="AnswerTitle"
              scale="point"
              padding={{ left: 75, right: 75 }}
              {...config.axis}
            />
            <YAxis {...config.axis} />
            <Tooltip {...config.tooltip} />
            <Legend {...config.legend} />
            <CartesianGrid strokeDasharray="3 3" stroke={config.colors.grid} />
            <Bar
              dataKey="AnswerCount"
              name={t('dashboard.dataLabels.answers')}
              fill={config.colors.primary}
              background={{ fill: config.colors.barBackground }}
              style={config.styles.noOutline}
              radius={[5, 5, 0, 0]}
              activeBar={{ fill: config.colors.primary, opacity: 0.6 }}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContent>

      {/* Footer */}
      <ChartFooter>{t('dashboard.questionnaire.showing')}</ChartFooter>
    </ChartContainer>
  );
};
